import {
  Box,
  Button,
  Checkbox,
  FormControl,
  HStack,
  Icon,
  Input,
  Stack,
  Text,
  VStack
} from "@chakra-ui/react";
import React, { useMemo } from "react";
import DatePicker from "react-datepicker";
import { IoClose } from "react-icons/io5";
import { useSelector } from "react-redux";

import { Select } from "@/components/forms/BaseForm/selects/Select";
import { Form } from "@/components/forms/Form/Form";
import { Option } from "@/components/forms/types";
import { useSideBar } from "@/components/layout/AppLayout";
import { FilterFields } from "@/hooks/crud/useCrudListFilter";
import { AccountWithCode } from "@/jenesys-types/data/common";
import {
  statusStyles,
  transactionSources
} from "@/jenesys-types/jenesys-entities/config";
import { selectAccountingShortData } from "@/store/features/clientSettings/connections/connections";
import "react-datepicker/dist/react-datepicker.css";
import { AdvancedFilter } from "./CrudListAdvancedFilter";
import { UploadedByFilter } from "./UploadedByFilter";

const createResetHandler =
  (
    handleInputChange: (
      event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => void,
    fieldName: string,
    resetValue: any = ""
  ) =>
  () => {
    handleInputChange({
      target: { name: fieldName, value: resetValue }
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

const createCollapseHandler =
  (
    setCollapsedSections: React.Dispatch<
      React.SetStateAction<Record<string, boolean>>
    >,
    sectionKey: string
  ) =>
  (collapsed: boolean) => {
    setCollapsedSections((prev) => ({ ...prev, [sectionKey]: collapsed }));
  };

const FilterFormControl: React.FC<{ children: React.ReactNode }> = ({
  children
}) => <FormControl>{children}</FormControl>;

export const AdvancedFilters: React.FC<{
  filters: FilterFields;
  handleInputChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => void;
  uploadedStartDate: Date | null;
  uploadedEndDate: Date | null;
  handleUploadedStartDateChange: (date: Date | null) => void;
  handleUploadedEndDateChange: (date: Date | null) => void;
  handleClear: () => void;
  handleSubmit: () => void;
  collapsedSections: Record<string, boolean>;
  setCollapsedSections: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  statusCounts: Record<string, number>;
  lastChosenStatus: string;
  resetCounter: number;
}> = ({
  filters,
  handleInputChange,
  uploadedStartDate,
  uploadedEndDate,
  handleUploadedStartDateChange,
  handleUploadedEndDateChange,
  handleClear,
  handleSubmit,
  collapsedSections,
  setCollapsedSections,
  statusCounts,
  lastChosenStatus,
  resetCounter
}) => {
  const { toggleSideBar } = useSideBar();
  const accountingData = useSelector(selectAccountingShortData);

  const accountCodes: AccountWithCode[] =
    accountingData?.accounts?.slice() ?? [];
  accountCodes.sort((a, b) => a.name.localeCompare(b.name));
  accountCodes.sort((a, b) => a.code?.localeCompare(b.code ?? "") ?? 0);

  const getActiveFilterCount = () => {
    let count = 0;
    // Check basic filters
    if (filters.supplier) count++;
    if (filters.total) count++;
    if (filters.dateFrom) count++;
    if (filters.dateTo) count++;

    // Check advanced filters
    if (filters.accountCode) count++;
    if (filters.vatAmount) count++;
    if (filters.source) count++;
    if (filters.status && filters.status.length > 0 && !lastChosenStatus)
      count++;
    if (uploadedStartDate || uploadedEndDate) count++;
    if (filters.uploadedBy) count++;
    if (filters.invoiceId) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  const handleStatusChange = (status: string) => {
    const currentStatuses = filters.status || [];
    const newStatuses = currentStatuses.includes(status)
      ? currentStatuses.filter((s) => s !== status)
      : [...currentStatuses, status];

    handleInputChange({
      target: { name: "status", value: newStatuses }
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  const handleClearWithCollapse = () => {
    setCollapsedSections({
      status: true,
      accountCode: true,
      vatAmount: true,
      total: true,
      uploadedDateRange: true,
      source: true,
      uploadedBy: true,
      invoiceId: true
    });
    handleClear();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleAccountCodeChange = (value: string | null) => {
    handleInputChange({
      target: { name: "accountCode", value: value || "" }
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  const handleSourceChange = (value: string | null) => {
    handleInputChange({
      target: { name: "source", value: value || "" }
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  const handleUploadedByChange = (value: string | null) => {
    handleInputChange({
      target: { name: "uploadedBy", value: value || "" }
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  const createFieldResetHandler = (fieldName: string, resetValue: any = "") =>
    createResetHandler(handleInputChange, fieldName, resetValue);

  const createSectionCollapseHandler = (sectionKey: string) =>
    createCollapseHandler(setCollapsedSections, sectionKey);

  const accountCodeOptions: Option<string>[] = useMemo(() => {
    return accountCodes.map((account: AccountWithCode) => ({
      value: account.id,
      label: account.code ? `${account.code} ${account.name}` : account.name
    }));
  }, [accountCodes]);

  const sourceOptions: Option<string>[] = useMemo(() => {
    return Object.entries(transactionSources).map(
      ([key, { displayLabel }]) => ({
        value: key,
        label: displayLabel
      })
    );
  }, []);

  return (
    <Box p={4}>
      <style>
        {
          // Override Chakra Collapse style so that non-native select options / date pickers are not clipped
          `
          .collapsible-filter-with-overflow {
            overflow: initial !important;
          }
        `
        }
      </style>
      <Form onSubmit={handleSubmit} initialValues={{}}>
        <VStack spacing={4} align="stretch">
          <HStack justify="space-between" align="center">
            <Text fontSize="lg" fontWeight="bold">
              Advanced Filters
            </Text>
            <Button
              size="sm"
              p={0}
              variant="ghost"
              onClick={toggleSideBar}
              aria-label="Close sidebar"
            >
              <Icon as={IoClose} boxSize={8} color="inherit" cursor="pointer" />
            </Button>
          </HStack>
          <Box borderBottom="1px" borderColor="gray.200" />

          <AdvancedFilter
            key={`status-filter-${resetCounter}`}
            heading="Status"
            isActive={!!(filters.status && filters.status.length > 0)}
            onReset={createFieldResetHandler("status", [])}
            isCollapsed={collapsedSections["status"]}
            onCollapseChange={createSectionCollapseHandler("status")}
            resetKey={resetCounter}
          >
            {lastChosenStatus ? (
              <Box
                p={3}
                bg="gray.50"
                rounded="md"
                border="1px"
                borderColor="gray.200"
              >
                <Text fontSize="sm" color="gray.600">
                  Status filter is disabled because you have selected "
                  {statusStyles[lastChosenStatus as keyof typeof statusStyles]
                    ?.text || lastChosenStatus}
                  " from the main view.
                </Text>
              </Box>
            ) : (
              <Stack spacing={2}>
                {Object.entries(statusStyles)
                  .filter(([key]) => key !== "") // Filter out the "All" option
                  .filter(([key]) => (statusCounts?.[key] ?? 0) > 0)
                  .sort((a, b) => a[1].index - b[1].index)
                  .map(([key, style]) => (
                    <Checkbox
                      key={key}
                      isChecked={filters.status?.includes(key)}
                      onChange={() => handleStatusChange(key)}
                      colorScheme="blue"
                    >
                      <HStack
                        spacing={2}
                        bg={style.bg}
                        color={style.color}
                        px={2}
                        py={1}
                        rounded="full"
                        fontSize="sm"
                      >
                        <Text>{style.text}</Text>
                      </HStack>
                    </Checkbox>
                  ))}
              </Stack>
            )}
          </AdvancedFilter>

          <AdvancedFilter
            heading="Account Code"
            isActive={!!filters.accountCode}
            onReset={createFieldResetHandler("accountCode")}
            isCollapsed={collapsedSections["accountCode"]}
            onCollapseChange={createSectionCollapseHandler("accountCode")}
            className="collapsible-filter-with-overflow"
            resetKey={resetCounter}
          >
            {accountCodes.length > 0 ? (
              <FilterFormControl>
                <Select
                  value={filters.accountCode || ""}
                  onChange={handleAccountCodeChange}
                  options={accountCodeOptions}
                  placeholder="Select account code..."
                  aria-label="Account Code"
                  isClearable={true}
                />
              </FilterFormControl>
            ) : (
              <Box
                p={3}
                bg="gray.50"
                rounded="md"
                border="1px"
                borderColor="gray.200"
              >
                <Text fontSize="sm" color="gray.600">
                  Connect to your accounting platform to filter by account code.
                </Text>
              </Box>
            )}
          </AdvancedFilter>

          <AdvancedFilter
            heading="VAT Amount"
            isActive={!!filters.vatAmount}
            onReset={createFieldResetHandler("vatAmount")}
            isCollapsed={collapsedSections["vatAmount"]}
            onCollapseChange={createSectionCollapseHandler("vatAmount")}
            resetKey={resetCounter}
          >
            <FilterFormControl>
              <Input
                name="vatAmount"
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                value={filters.vatAmount || ""}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Filter by VAT amount..."
                aria-label="VAT Amount"
              />
            </FilterFormControl>
          </AdvancedFilter>

          <AdvancedFilter
            heading="Total"
            isActive={!!filters.total}
            onReset={createFieldResetHandler("total")}
            isCollapsed={collapsedSections["total"]}
            onCollapseChange={createSectionCollapseHandler("total")}
            resetKey={resetCounter}
          >
            <FilterFormControl>
              <Input
                name="total"
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                value={filters.total || ""}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Filter by total..."
                aria-label="Total"
              />
            </FilterFormControl>
          </AdvancedFilter>

          <AdvancedFilter
            heading="Uploaded Date"
            isActive={!!(uploadedStartDate || uploadedEndDate)}
            onReset={() => {
              handleUploadedStartDateChange(null);
              handleUploadedEndDateChange(null);
            }}
            isCollapsed={collapsedSections["uploadedDateRange"]}
            onCollapseChange={createSectionCollapseHandler("uploadedDateRange")}
            className="collapsible-filter-with-overflow"
            resetKey={resetCounter}
          >
            <FilterFormControl>
              <Box
                as={DatePicker}
                selected={uploadedStartDate}
                onChange={handleUploadedStartDateChange}
                selectsStart
                startDate={uploadedStartDate}
                endDate={uploadedEndDate}
                dateFormat="dd/MM/yyyy"
                customInput={
                  <Input
                    onKeyDown={handleKeyDown}
                    aria-label="Uploaded Date From"
                  />
                }
                placeholderText="From date..."
                css={{
                  width: "100%",
                  height: "2.5rem",
                  fontSize: "1rem",
                  color: "#2d3748",
                  "&::placeholder": {
                    color: "#a0aec0"
                  }
                }}
              />
            </FilterFormControl>
            <FilterFormControl>
              <Box
                as={DatePicker}
                selected={uploadedEndDate}
                onChange={handleUploadedEndDateChange}
                selectsEnd
                startDate={uploadedStartDate}
                endDate={uploadedEndDate}
                {...(uploadedStartDate ? { minDate: uploadedStartDate } : {})}
                dateFormat="dd/MM/yyyy"
                customInput={
                  <Input
                    onKeyDown={handleKeyDown}
                    aria-label="Uploaded Date To"
                  />
                }
                placeholderText="To date..."
                css={{
                  width: "100%",
                  height: "2.5rem",
                  fontSize: "1rem",
                  color: "#2d3748",
                  "&::placeholder": {
                    color: "#a0aec0"
                  }
                }}
              />
            </FilterFormControl>
          </AdvancedFilter>

          <AdvancedFilter
            heading="Source"
            isActive={!!filters.source}
            onReset={createFieldResetHandler("source")}
            isCollapsed={collapsedSections["source"]}
            onCollapseChange={createSectionCollapseHandler("source")}
            className="collapsible-filter-with-overflow"
            resetKey={resetCounter}
          >
            <FilterFormControl>
              <Select
                value={filters.source || ""}
                onChange={handleSourceChange}
                options={sourceOptions}
                aria-label="Source"
                isClearable={true}
              />
            </FilterFormControl>
          </AdvancedFilter>

          <AdvancedFilter
            heading="Uploaded By"
            isActive={!!filters.uploadedBy}
            onReset={createFieldResetHandler("uploadedBy")}
            isCollapsed={collapsedSections["uploadedBy"]}
            onCollapseChange={createSectionCollapseHandler("uploadedBy")}
            resetKey={resetCounter}
            className="collapsible-filter-with-overflow"
          >
            <FilterFormControl>
              <UploadedByFilter
                value={filters.uploadedBy || ""}
                onChange={handleUploadedByChange}
              />
            </FilterFormControl>
          </AdvancedFilter>

          <AdvancedFilter
            heading="Document Number"
            isActive={!!filters.invoiceId}
            onReset={createFieldResetHandler("invoiceId")}
            isCollapsed={collapsedSections["invoiceId"]}
            onCollapseChange={createSectionCollapseHandler("invoiceId")}
            resetKey={resetCounter}
          >
            <FilterFormControl>
              <Input
                name="invoiceId"
                value={filters.invoiceId || ""}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Document Number..."
                aria-label="Document Number"
              />
            </FilterFormControl>
          </AdvancedFilter>

          <HStack spacing={4} width="100%">
            {activeFilterCount > 0 && (
              <Button
                height="2.5rem"
                variant="ghost"
                colorScheme="blue"
                onClick={handleClearWithCollapse}
                flex={1}
              >
                Clear Filters
              </Button>
            )}
            <Button
              height="2.5rem"
              colorScheme="blue"
              onClick={handleSubmit}
              flex={1}
              type="submit"
            >
              Apply{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
            </Button>
          </HStack>
        </VStack>
      </Form>
    </Box>
  );
};
